import Bluebird from 'bluebird';
import React from 'react';
import { Button, Container, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { translate } from 'translations/translator';
import { useDispatch, useSelector, batch } from 'react-redux';
import { getWorkbookTheme } from 'util/workbook-util';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import useLessonAssignment from 'state/providers/LessonAssignmentProvider';
import { useAlert, AlertVariant, AlertStyleVariant } from 'state/providers/AlertProvider';
import { BaseModalTitle } from 'components/@common/Modals/BaseModalTitle';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { NoAssignmentModal } from 'components/@common/Modals/NoAssignmentModal';
import {
  createUserAssignment,
  createUserAssignmentInstructions,
  getUserAssignmentsView,
  saveUserAssignment,
  deleteUserAssignmentInstructions
} from 'state/modules/entity/thunks/assignment';
import { calcDuration } from 'util/assignment-util';
import { parseQueryParams } from 'util/query-params-util';
import { AssignmentEngineParams } from 'state/providers/LessonAssignmentProvider/types';
import { createEvent } from 'api';
import { selectClassroom as setClassroom } from 'state/modules/app';
import { AddActivityModal } from 'components/@common/Modals/AddActivitiesModal';
import LessonAssignmentTranslationVariant from 'translations/variants/lessonAssignment';
import { HwtButton } from 'components/@common/Buttons/HwtButton';

const {
  addClassesAndStudents,
  assignLesson,
  editAssignment,
  cancel,
  reviewAndAssign,
  addActivityTo,
  activitySuccessfullyAddedTo
} = LessonAssignmentTranslationVariant;

const getTranslation = (key: LessonAssignmentTranslationVariant, options?) =>
  translate('lessonAssignment', key, options);

export const LessonAssignmentFooter = () => {
  const { bookCode, edition } = useParams<{ bookCode: string; edition: string }>();
  const navigate = useNavigate();

  const location = useLocation();
  const theme = useSelector(getWorkbookTheme(bookCode ?? ''));
  const classes = useStyles(theme);
  const dispatch = useDispatch() as any;

  const [, { openAlert }] = useAlert();
  const [, { openModal }] = useModal();
  const [
    { stepperState, stepOneFormState, lessonAssignmentState },
    { stepperDispatch, lessonAssignmentDispatch, stepOneFormDispatch }
  ] = useLessonAssignment();
  const handleCancel = () => {
    navigate(`/books/${bookCode}/${edition}/lesson-planner`);
  };

  const handleAssignedDuration = assignedDays => {
    const assignmentsDurationExceed = assignedDays.some(x => calcDuration(x) > 30);

    if (assignmentsDurationExceed) {
      openAlert({
        title: getTranslation(LessonAssignmentTranslationVariant.durationAlertTitle),
        body: <Box>{getTranslation(LessonAssignmentTranslationVariant.durationAlertBody)}</Box>,
        variant: AlertVariant.WARNING
      });
    }
  };

  const createTelemetryEvent = async (
    type,
    {
      id: assignmentId,
      classroomId,
      studentIds,
      teachingOrderLessonId,
      title,
      instructions,
      audioFiles,
      dateAssigned,
      dateDue,
      maxAttempts,
      sections
    }
  ) =>
    createEvent(type, {
      assignmentId,
      classroomId,
      studentIds,
      teachingOrderLessonId,
      title,
      instructions,
      ...(audioFiles && { audioFiles: audioFiles.length }),
      dateAssigned,
      dateDue,
      maxAttempts,
      numActivities: sections.reduce((acc, section) => acc + section.activities.length, 0)
    });

  const handleCreateAssignment = () => {
    batch(async () => {
      const assignments = lessonAssignmentDispatch.getAssignmentPayload();
      const {
        data: { assignments: createdAssignments }
      } = (await dispatch(createUserAssignment(assignments))) as any;

      const assignmentsWithAudioFiles = await lessonAssignmentDispatch.getTeacherInstructionPayload(
        createdAssignments,
        false
      );

      await Bluebird.each(assignmentsWithAudioFiles, async ({ assignmentId, audioFiles }) => {
        const [audioFile] = audioFiles;
        await dispatch(createUserAssignmentInstructions(assignmentId, audioFile));

        // add audioFiles property for telemetry
        const createdAssignment = createdAssignments.find(({ id }) => id === assignmentId);
        createdAssignment.audioFiles = audioFiles;
      });

      createdAssignments.forEach(assignment =>
        createTelemetryEvent('assignmentCreated', assignment)
      );

      dispatch(getUserAssignmentsView());
      navigate(`/books/${bookCode}/${edition}/lesson-planner`);
    });

    openAlert({
      body: getTranslation(LessonAssignmentTranslationVariant.assignmentCreatedSuccess),
      variant: AlertVariant.SUCCESS,
      styleVariant: AlertStyleVariant.FILLED
    });
  };

  const handleUpdateAssignment = (assignmentId, updates, params?) =>
    batch(async () => {
      const {
        data: { assignments: updatedAssignments }
      }: any = await dispatch(saveUserAssignment(assignmentId, updates, params));

      const updatedAssignment = updatedAssignments.find(({ id }) => id === assignmentId);
      const assignmentsWithAudioFiles = await lessonAssignmentDispatch.getTeacherInstructionPayload(
        [updatedAssignment],
        true
      );

      if (!assignmentsWithAudioFiles.length) {
        await dispatch(deleteUserAssignmentInstructions(assignmentId));
      } else {
        const [{ audioFiles }] = assignmentsWithAudioFiles;
        const [audioFile] = audioFiles;

        // if this is a new file we need to delete any existing file and create the new one
        if (!audioFile.id) {
          await dispatch(deleteUserAssignmentInstructions(assignmentId));
          await dispatch(createUserAssignmentInstructions(assignmentId, audioFile));
        }

        // add audioFiles property for telemetry
        updatedAssignment.audioFiles = audioFiles;
      }

      createTelemetryEvent('assignmentUpdated', updatedAssignment);

      dispatch(getUserAssignmentsView());

      openAlert({
        body: 'Assignment Updated Successfully',
        variant: AlertVariant.SUCCESS,
        styleVariant: AlertStyleVariant.FILLED
      });

      navigate(`/books/${bookCode}/${edition}/lesson-planner`);
    });

  const getButtonLabel = ({ activeStep }) => {
    const routeParams = parseQueryParams(location.search);
    switch (activeStep) {
      case 1:
        return getTranslation(addClassesAndStudents);
      case 2:
        return getTranslation(reviewAndAssign);
      case 3:
        return routeParams?.assignment === AssignmentEngineParams.EDIT
          ? getTranslation(editAssignment)
          : getTranslation(assignLesson);
      default:
        return getTranslation(addClassesAndStudents);
    }
  };

  const handleStepThreeSubmitActions = async () => {
    const routeParams = parseQueryParams(location.search);

    if (lessonAssignmentState?.studentsAndClassrooms?.length < 2) {
      dispatch(setClassroom(lessonAssignmentState?.studentsAndClassrooms?.[0].value.id));
    }

    if (routeParams?.assignment === AssignmentEngineParams.EDIT) {
      const [{ teachingOrderLessonId, ...updatedAssignment }] =
        lessonAssignmentDispatch.getAssignmentPayload();

      const assignmentId = Number.parseInt(routeParams?.assignmentId as any);
      await handleUpdateAssignment(
        assignmentId,
        {
          id: assignmentId,
          ...updatedAssignment
        },
        { force: true }
      );
      return;
    }

    await handleCreateAssignment();
    return;
  };

  const handleAddActivity = () => {
    const onAddActivities = async activities => {
      await stepOneFormDispatch.onAddActivity(0, activities);

      openAlert({
        body: `${getTranslation(activitySuccessfullyAddedTo)} ${
          LessonAssignmentTranslationVariant.warmup
        }`,
        variant: AlertVariant.SUCCESS
      });
    };

    openModal({
      Title: () => (
        <BaseModalTitle
          text={`${getTranslation(addActivityTo)} ${LessonAssignmentTranslationVariant.warmup}`}
        />
      ),
      body: <AddActivityModal handleAddActivity={onAddActivities} />
    });
  };

  const handleCompleteStep = async () => {
    if (stepperState.activeStep === 3) {
      await handleStepThreeSubmitActions();
      return;
    }

    const errors = await stepperDispatch.completeActiveStepAndSetNextStep(
      stepperState.activeStep + 1
    );

    const form = Object.values(stepOneFormState.form) as any[];
    const assignedDays: any = form.filter(x => x.assigned === true);
    const assignedHasActivities: any = assignedDays.filter(
      x => x?.lesson?.sections?.filter(s => s.activities.length > 0).length > 0
    );

    if (errors) {
      openAlert({ body: JSON.stringify(errors), variant: AlertVariant.ERROR });
    }

    if (!assignedDays.length || assignedHasActivities.length !== assignedDays.length) {
      openModal({
        Title: () => <BaseModalTitle text="No Activity in Assignment" />,
        body: <NoAssignmentModal onClose={handleAddActivity} />
      });
    }

    handleAssignedDuration(assignedDays);
  };

  return (
    <Container className={classes.container} maxWidth="xl">
      <Button
        variant="contained"
        fullWidth={true}
        style={{
          width: '116px',
          textTransform: 'none',
          fontSize: '16px',
          backgroundColor: '#000000'
        }}
        onClick={handleCancel}
      >
        {getTranslation(cancel)}
      </Button>
      <HwtButton
        variant="contained"
        fullWidth={true}
        style={{ width: '275px', fontSize: '16px', whiteSpace: 'nowrap' }}
        onClick={handleCompleteStep}
      >
        <FontAwesomeIcon icon={faArrowRight} fontSize="14" style={{ marginRight: '10px' }} />
        {getButtonLabel(stepperState)}
      </HwtButton>
    </Container>
  );
};

const useStyles = makeStyles((theme: any) => ({
  container: (workbookTheme: any) =>
    ({
      display: 'flex !important',
      flex: 1,
      justifyContent: 'space-between !important',
      padding: '8px 20px',
      position: 'sticky !important',
      bottom: 0,
      backgroundColor: `${workbookTheme?.primaryColor || theme.palette.link.main}`
    } as any)
}));
